import type { User } from '@shared/models/user';

/**
 * Resolve the dashboard-nested billing route segments for the current user.
 *
 * Priority order:
 *   1. Current router URL — if the user is already at /dashboard/{org}/{branch}/...
 *      these IDs are guaranteed to be the active session context.
 *      PinnedBranch.branch() can return undefined for users whose role has
 *      no `shop` and no `shops[0]` even when the URL clearly has a branch
 *      ID (caused the trial banner's 'purchase here' link to render as
 *      bare /dashboard, and PaywallRedirector to bail without navigating).
 *   2. Pinned org/branch from PinnedBranch (covers cases where the user
 *      isn't on /dashboard yet, e.g. right after login from /auth/...).
 *   3. The user's first role's organisation + shop / shops[0]
 *      (last-ditch fallback).
 *
 * Returns `['/dashboard']` only as a defensive fallback — a route array
 * containing a falsy org/branch would render a broken URL. Callers can
 * treat `['/dashboard']` as "couldn't resolve" and either skip the
 * navigation (PaywallRedirector) or show a degraded link (TrialBanner).
 */
export function resolveBillingRoute(
  currentUrl: string | null | undefined,
  user: User | null | undefined,
  pinnedOrg: string | null | undefined,
  pinnedBranch: string | null | undefined,
): readonly string[] {
  const urlMatch = currentUrl?.match(/^\/dashboard\/([^/?#]+)\/([^/?#]+)/);
  const role = user?.roles?.[0];

  const org = urlMatch?.[1] ?? pinnedOrg ?? role?.organisation;
  const branch =
    urlMatch?.[2] ?? pinnedBranch ?? role?.shop ?? role?.shops?.[0];

  if (!org || !branch) return ['/dashboard'];
  return ['/dashboard', org, branch, 'settings', 'billing'];
}
